import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Mail, Phone, MessageCircle, MapPin, Linkedin, Clock } from "lucide-react";
import { toast } from "sonner";
import { ChapterHeading } from "@/components/ChapterHeading";
import { CONTACT, PROFILE } from "@/data/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", message: "" };

const Row = ({ icon: Icon, label, children, testId }) => (
  <div className="flex gap-5 py-5 border-b hairline" data-testid={testId}>
    <Icon size={16} className="text-[var(--accent)] mt-1 shrink-0" />
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 text-sm md:text-base">{children}</div>
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Message logged. Michael will reply within 48 hours.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Check the fields and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-dark border-t hairline px-6 md:px-12 py-24 md:py-40">
      <ChapterHeading num="09" label="Contact" title={["Put Warden Offshore", "on your next hitch"]} />
      <div className="mt-16 md:mt-24 grid grid-cols-12 gap-10 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <div className="border-t hairline">
            <Row icon={MapPin} label="Company" testId="contact-company">
              <span className="font-display uppercase text-xl">{CONTACT.company}</span>
              <div className="text-muted text-sm mt-1">{PROFILE.companyTag} · Founder: {PROFILE.name}</div>
            </Row>
            <Row icon={Mail} label="Email" testId="contact-email">
              <a href={`mailto:${CONTACT.email}`} className="hover:!text-[var(--accent)] transition-colors duration-300" data-testid="contact-email-link">{CONTACT.email}</a>
            </Row>
            <Row icon={Phone} label="Phone" testId="contact-phone">
              <a href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`} className="hover:!text-[var(--accent)] transition-colors duration-300">{CONTACT.phone}</a>
            </Row>
            <Row icon={MessageCircle} label="WhatsApp" testId="contact-whatsapp">
              <a href={`https://wa.me/${CONTACT.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="hover:!text-[var(--accent)] transition-colors duration-300">{CONTACT.whatsapp}</a>
            </Row>
            <Row icon={MapPin} label="Office" testId="contact-address">
              {CONTACT.address.map((l) => <div key={l}>{l}</div>)}
            </Row>
            {CONTACT.linkedin && (
              <Row icon={Linkedin} label="LinkedIn" testId="contact-linkedin">
                <a href={`https://${CONTACT.linkedin}`} target="_blank" rel="noreferrer" className="hover:!text-[var(--accent)] transition-colors duration-300">{CONTACT.linkedin}</a>
              </Row>
            )}
            <Row icon={Clock} label="Availability" testId="contact-hours">
              {CONTACT.hours}
            </Row>
          </div>
          {CONTACT.placeholderNote && <p className="eyebrow mt-4 !text-[var(--accent-2)]" data-testid="contact-placeholder-note">{CONTACT.placeholderNote}</p>}
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="col-span-12 lg:col-span-6 lg:col-start-7 grid grid-cols-2 gap-x-8 gap-y-6 content-start">
          <p className="col-span-2 eyebrow">Send a brief</p>
          <input data-testid="contact-name-input" className="field col-span-2 md:col-span-1" placeholder="Name" value={form.name} onChange={set("name")} required minLength={2} />
          <input data-testid="contact-email-input" className="field col-span-2 md:col-span-1" type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
          <input data-testid="contact-company-input" className="field col-span-2" placeholder="Company / Operator" value={form.company} onChange={set("company")} />
          <textarea data-testid="contact-message-input" className="field col-span-2 resize-none" rows={5} placeholder="Rig, location, dates and scope" value={form.message} onChange={set("message")} required minLength={10} />
          <div className="col-span-2 flex items-center justify-between pt-4">
            <span className="eyebrow" data-testid="contact-status">{sent ? "Received — 48h response" : "Stored securely"}</span>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="btn-industrial border hairline px-8 py-4 eyebrow !text-white flex items-center gap-3 disabled:opacity-50"
            >
              {sending ? "Sending" : "Send brief"} <ArrowUpRight size={14} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
